import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';
import { propertyNameQueryForFastApi } from './cashReconPropertyApi';
import { appendCashReconLedgerCoaQueryParams } from './cashReconLedgerScope';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateAbortButtonSx,
  harmonateCancelButtonSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonateDialogContentSx,
  harmonateDialogFormContentSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateFormFieldsSx,
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonatePlainIconButtonSx,
  harmonateProceedButtonSx,
  harmonateSelectMenuProps,
} from '../../../theme/harmonateStyles';

const emptyForm = {
  hotel: '',
  monthYear: '',
  bankAccountNo: '',
  bank: '',
  glAccountId: '',
};

const pickOperatingCashGlAccountFromPropertyRow = (row) => {
  if (!row) return '';
  const direct = row.OPERATING_CASH_GL_ACCOUNT ?? row.operating_cash_gl_account;
  if (direct != null && String(direct).trim()) return String(direct).trim();
  try {
    const content = typeof row.CONTENT === 'string' ? JSON.parse(row.CONTENT) : row.CONTENT;
    if (content?.OPERATING_CASH_GL_ACCOUNT != null && String(content.OPERATING_CASH_GL_ACCOUNT).trim()) {
      return String(content.OPERATING_CASH_GL_ACCOUNT).trim();
    }
  } catch (_) {
    /* ignore malformed CONTENT */
  }
  return '';
};

const pickBankBrandFromChaseRow = (row) => {
  if (!row) return '';
  const brand = row.BANK_BRAND ?? row.bank_brand;
  return brand != null ? String(brand).trim() : '';
};

const isChaseBankBrand = (brand) => {
  const normalized = String(brand ?? '').toLowerCase();
  return /jp\s*morgan\s*chase|jp\s*morgan|jpmc|\bchase\b/.test(normalized);
};

const resolveBankIdFromBrand = (bankList, brand) => {
  if (!isChaseBankBrand(brand) || !Array.isArray(bankList) || !bankList.length) return '';
  const match = bankList.find((bank) => {
    const hay = `${bank.bankName || ''} ${bank.bankShortName || ''} ${bank.bankCode || ''}`.toLowerCase();
    return /jpmc|jp\s*morgan|\bchase\b/.test(hay);
  });
  return match?.bankId != null ? String(match.bankId) : '';
};

const resolveGlAccountIdFromGlCode = (accounts, glCode) => {
  const needle = String(glCode ?? '').trim();
  if (!needle || !Array.isArray(accounts) || !accounts.length) return '';
  const normalized = needle.toLowerCase();
  const match =
    accounts.find((a) => String(a.glCode ?? '').trim().toLowerCase() === normalized) ||
    accounts.find((a) => String(a.glAccount ?? '').trim().toLowerCase() === normalized);
  return match?.ledgerAccountId != null ? String(match.ledgerAccountId) : '';
};

const propertyLabel = (option) => {
  if (!option) return '';
  const code = option.PROPERTY_CODE || option.propertyCode || '';
  const name = option.PROPERTY_NAME || option.propertyName || '';
  return code && name ? `${code} - ${name}` : name || code || '';
};

const AccountMapping = () => {
  const { canAdd, canEdit, canDelete } = useFunctionPermissions(FC.CASH_RECONCILIATION);
  const navigate = useNavigate();
  const { toastState, showSuccess, showError, hideToast } = useToast();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectionModel, setSelectionModel] = useState([]);
  const [properties, setProperties] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [banks, setBanks] = useState([]);
  const [banksLoading, setBanksLoading] = useState(false);
  const [glAccounts, setGlAccounts] = useState([]);
  const [glAccountsLoading, setGlAccountsLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [mode, setMode] = useState('add');
  const [form, setForm] = useState(emptyForm);
  const [editingRow, setEditingRow] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [propertyAccountLoading, setPropertyAccountLoading] = useState(false);
  const [suggestedGlCode, setSuggestedGlCode] = useState('');
  const [suggestedBankBrand, setSuggestedBankBrand] = useState('');

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [rowPendingDelete, setRowPendingDelete] = useState(null);

  const selectedRow = useMemo(
    () => rows.find((row) => row.id === selectionModel[0]),
    [rows, selectionModel]
  );

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      Authorization: token ? `Bearer ${token}` : '',
      'Content-Type': 'application/json',
    };
  };

  const fetchProperties = async () => {
    try {
      setPropertiesLoading(true);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      if (!token || !tenantId) return;
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/user-tenant-properties?tenant_id=${tenantId}&user_id=${localStorage.getItem('user_id')}`,
        { method: 'GET', headers: authHeaders() }
      );
      if (!resp.ok) return;
      const data = await resp.json();
      if (Array.isArray(data)) {
        setProperties(
          data.map((property) => ({
            PROPERTY_ID: property.propertyId,
            PROPERTY_NAME: property.propertyName,
            PROPERTY_CODE: property.propertyCode,
            ...property,
          }))
        );
      }
    } catch (err) {
      console.error('Error fetching properties:', err);
      setProperties([]);
    } finally {
      setPropertiesLoading(false);
    }
  };

  const fetchBanks = async () => {
    try {
      setBanksLoading(true);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      if (!token || !tenantId) return;
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/banks/by-tenant-and-status?tenant_id=${tenantId}&status=ACTIVE`,
        { headers: authHeaders() }
      );
      if (!resp.ok) {
        setBanks([]);
        return;
      }
      const data = await resp.json();
      setBanks(data || []);
    } catch (err) {
      console.error('Error fetching banks:', err);
      setBanks([]);
    } finally {
      setBanksLoading(false);
    }
  };

  const fetchGlAccounts = async () => {
    try {
      setGlAccountsLoading(true);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      if (!token || !tenantId) {
        setGlAccounts([]);
        return;
      }
      const baseUrl = process.env.REACT_APP_BACKEND_URL;
      const url = await appendCashReconLedgerCoaQueryParams(
        `${baseUrl}conductor/cash-recon/chart-of-account/by-tenant-with-account-type-property?tenant_id=${tenantId}`,
        { baseUrl, tenantId, token }
      );
      const resp = await fetch(url, { method: 'GET', headers: authHeaders() });
      if (!resp.ok) {
        setGlAccounts([]);
        return;
      }
      const data = await resp.json();
      setGlAccounts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching GL accounts:', err);
      setGlAccounts([]);
    } finally {
      setGlAccountsLoading(false);
    }
  };

  const fetchMappings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      if (!token || !tenantId) {
        throw new Error('Missing authentication or tenant information');
      }
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon-mapping-headers/filter?tenant_id=${tenantId}`,
        { method: 'GET', headers: authHeaders() }
      );
      if (!resp.ok) {
        throw new Error(`Failed to load mappings: ${resp.status}`);
      }
      const data = await resp.json();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching mapping headers:', err);
      setRows([]);
      showError(err.message || 'Failed to load mappings');
    } finally {
      setLoading(false);
    }
  };

  const fetchPropertyAccountList = async (hotelSelection) => {
    const apiPropertyName = propertyNameQueryForFastApi(hotelSelection, properties);
    if (!apiPropertyName) return null;
    const token = localStorage.getItem('token');
    const tenantId = localStorage.getItem('tenant_integration');
    if (!token || !tenantId) return null;
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/cash-recon/property-account-list?tenant_id=${encodeURIComponent(tenantId)}&property_name=${encodeURIComponent(apiPropertyName)}`;
      const resp = await fetch(url, { method: 'GET', headers: authHeaders() });
      if (!resp.ok) return null;
      const json = await resp.json();
      if (json.status === 'success' && Array.isArray(json.data) && json.data.length > 0) {
        const first = json.data[0];
        return {
          operatingAccount: first.OPERATING_ACCOUNT != null ? String(first.OPERATING_ACCOUNT) : '',
          operatingCashGlAccount: pickOperatingCashGlAccountFromPropertyRow(first),
        };
      }
      return { operatingAccount: '', operatingCashGlAccount: '' };
    } catch (err) {
      console.error('Error fetching property account list:', err);
      return null;
    }
  };

  const fetchChaseBankBrand = async (operatingAccount) => {
    const rawAccount = String(operatingAccount || '').trim();
    const account = rawAccount.replace(/^0+/, '') || rawAccount;
    if (!account) return '';
    const token = localStorage.getItem('token');
    const tenantId = localStorage.getItem('tenant_integration');
    if (!token || !tenantId) return '';
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/cash-recon/bank-data-chase-summary?tenant_id=${encodeURIComponent(tenantId)}&customer_account_number=${encodeURIComponent(account)}&limit=1`;
      const resp = await fetch(url, { method: 'GET', headers: authHeaders() });
      if (!resp.ok) return '';
      const json = await resp.json();
      if (json.status === 'success' && Array.isArray(json.data) && json.data.length > 0) {
        return pickBankBrandFromChaseRow(json.data[0]);
      }
      return '';
    } catch (err) {
      console.error('Error fetching Chase bank brand:', err);
      return '';
    }
  };

  const fetchBalanceData = async (propertyId, monthYear) => {
    const token = localStorage.getItem('token');
    const tenantId = localStorage.getItem('tenant_id');
    if (!token || !tenantId || !propertyId || !monthYear) return null;
    const [year, month] = monthYear.split('-');
    const startDate = `${year}-${month}-01`;
    const lastDay = new Date(year, parseInt(month, 10), 0).getDate();
    const endDate = `${year}-${month}-${String(lastDay).padStart(2, '0')}`;
    try {
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/recon-mapping-details/balance?tenant_id=${tenantId}&property_id=${propertyId}&from_date=${startDate}&to_date=${endDate}`,
        { method: 'GET', headers: authHeaders() }
      );
      if (!resp.ok) return null;
      const data = await resp.json();
      return data?.status === 'success' ? data : null;
    } catch (err) {
      console.error('Error fetching balance data:', err);
      return null;
    }
  };

  useEffect(() => {
    fetchProperties();
    fetchBanks();
    fetchGlAccounts();
    fetchMappings();
  }, []);

  useEffect(() => {
    if (!dialogOpen || mode !== 'add') return;
    const hotel = form.hotel;
    if (!hotel) {
      setSuggestedGlCode('');
      setSuggestedBankBrand('');
      return;
    }
    let cancelled = false;
    setPropertyAccountLoading(true);
    setSuggestedGlCode('');
    setSuggestedBankBrand('');
    setForm((prev) => ({ ...prev, bankAccountNo: '', bank: '', glAccountId: '' }));
    (async () => {
      const result = await fetchPropertyAccountList(hotel);
      if (cancelled) return;
      if (!result) {
        setPropertyAccountLoading(false);
        return;
      }
      const { operatingAccount, operatingCashGlAccount } = result;
      const bankBrand = await fetchChaseBankBrand(operatingAccount);
      if (cancelled) return;
      setPropertyAccountLoading(false);
      setSuggestedGlCode(operatingCashGlAccount || '');
      setSuggestedBankBrand(bankBrand || '');
      setForm((prev) => ({
        ...prev,
        bankAccountNo: operatingAccount || '',
        bank: resolveBankIdFromBrand(banks, bankBrand),
        glAccountId: resolveGlAccountIdFromGlCode(glAccounts, operatingCashGlAccount),
      }));
    })().catch(() => {
      if (!cancelled) setPropertyAccountLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [dialogOpen, mode, form.hotel]);

  useEffect(() => {
    if (!dialogOpen || mode !== 'add' || !suggestedGlCode || glAccountsLoading) return;
    const matchId = resolveGlAccountIdFromGlCode(glAccounts, suggestedGlCode);
    if (!matchId) return;
    setForm((prev) => {
      if (!prev.hotel || prev.glAccountId) return prev;
      return { ...prev, glAccountId: matchId };
    });
  }, [dialogOpen, mode, suggestedGlCode, glAccounts, glAccountsLoading]);

  useEffect(() => {
    if (!dialogOpen || mode !== 'add' || !suggestedBankBrand || banksLoading) return;
    const matchId = resolveBankIdFromBrand(banks, suggestedBankBrand);
    if (!matchId) return;
    setForm((prev) => {
      if (!prev.hotel || prev.bank) return prev;
      return { ...prev, bank: matchId };
    });
  }, [dialogOpen, mode, suggestedBankBrand, banks, banksLoading]);

  useEffect(() => {
    if (!dialogOpen || mode !== 'edit' || !editingRow || glAccountsLoading) return;
    const matchId =
      resolveGlAccountIdFromGlCode(glAccounts, editingRow.glAccountNo) ||
      resolveGlAccountIdFromGlCode(glAccounts, editingRow.glAccountName);
    if (!matchId) return;
    setForm((prev) => (prev.glAccountId ? prev : { ...prev, glAccountId: matchId }));
  }, [dialogOpen, mode, editingRow, glAccounts, glAccountsLoading]);

  const bankNameById = useMemo(() => {
    const map = {};
    banks.forEach((bank) => {
      if (bank?.bankId) map[String(bank.bankId)] = bank.bankName || '';
    });
    return map;
  }, [banks]);

  const propertyById = useMemo(() => {
    const map = {};
    properties.forEach((property) => {
      const id = property.PROPERTY_ID || property.propertyId;
      if (id) map[String(id)] = property;
    });
    return map;
  }, [properties]);

  const openAdd = () => {
    if (!canAdd) return;
    setMode('add');
    setEditingRow(null);
    setForm(emptyForm);
    setSuggestedGlCode('');
    setSuggestedBankBrand('');
    setDialogOpen(true);
  };

  const openEdit = () => {
    if (!canEdit || !selectedRow) return;
    const property = propertyById[String(selectedRow.propertyId)];
    const hotel = property?.PROPERTY_NAME || property?.propertyName || '';
    const glAccountId =
      resolveGlAccountIdFromGlCode(glAccounts, selectedRow.glAccountNo) ||
      resolveGlAccountIdFromGlCode(glAccounts, selectedRow.glAccountName);
    setMode('edit');
    setEditingRow(selectedRow);
    setForm({
      hotel,
      monthYear: selectedRow.monthYear || '',
      bankAccountNo: selectedRow.bankAccountNo || '',
      bank: selectedRow.bankId || '',
      glAccountId,
    });
    setSuggestedGlCode('');
    setSuggestedBankBrand('');
    setDialogOpen(true);
  };

  const findProperty = (hotel) =>
    properties.find((p) => p.PROPERTY_NAME === hotel || p.propertyName === hotel);

  const selectedGlAccount = glAccounts.find(
    (account) => String(account.ledgerAccountId) === String(form.glAccountId)
  );

  const handleSave = async () => {
    if ((mode === 'add' && !canAdd) || (mode === 'edit' && !canEdit)) return;
    if (
      !form.hotel ||
      !form.monthYear ||
      !String(form.bankAccountNo || '').trim() ||
      !form.bank ||
      !form.glAccountId
    ) {
      showError('Please fill all required fields');
      return;
    }

    const selectedProp = findProperty(form.hotel);
    const propertyId = selectedProp?.PROPERTY_ID || selectedProp?.propertyId;
    if (!propertyId) {
      showError('Please select a valid hotel');
      return;
    }

    const tenantId = localStorage.getItem('tenant_id');
    const token = localStorage.getItem('token');
    if (!token || !tenantId) {
      showError('Missing authentication or tenant information');
      return;
    }

    setSubmitting(true);
    try {
      const glAccountNo = selectedGlAccount?.glCode ?? '';
      const glAccountName =
        selectedGlAccount?.glDescription ?? (selectedGlAccount?.glAccount || '');
      const selectedBank = banks.find((b) => String(b.bankId) === String(form.bank));

      if (mode === 'add') {
        const duplicate = rows.find(
          (row) =>
            String(row.propertyId) === String(propertyId) &&
            row.monthYear === form.monthYear
        );
        if (duplicate) {
          throw new Error('A mapping already exists for this hotel and month. Edit that row instead.');
        }
        const balanceInfo = await fetchBalanceData(propertyId, form.monthYear);
        const requestBody = {
          propertyId,
          monthYear: form.monthYear,
          balance: balanceInfo?.bankBalance || 0,
          approveStatus: null,
          bankId: form.bank,
          bankName: selectedBank?.bankName || null,
          tenantId,
          bankAccountNo: form.bankAccountNo.trim(),
          glAccountNo,
          glAccountName,
          createdBy: localStorage.getItem('user_id') || null,
          updatedBy: localStorage.getItem('user_id') || null,
        };
        const resp = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon-mapping-headers`,
          {
            method: 'POST',
            headers: authHeaders(),
            body: JSON.stringify(requestBody),
          }
        );
        if (!resp.ok) {
          const errorData = await resp.json().catch(() => ({}));
          throw new Error(errorData.error || errorData.message || `Failed to add mapping: ${resp.status}`);
        }
        showSuccess('Mapping saved successfully');
      } else {
        const requestBody = {
          propertyId: editingRow.propertyId,
          monthYear: editingRow.monthYear,
          balance: editingRow.balance ?? 0,
          approveStatus: editingRow.approveStatus || null,
          bankId: form.bank,
          bankName: selectedBank?.bankName || null,
          tenantId: editingRow.tenantId || tenantId,
          bankAccountNo: form.bankAccountNo.trim(),
          glAccountNo,
          glAccountName,
        };
        const resp = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon-mapping-headers/${editingRow.id}`,
          {
            method: 'PUT',
            headers: authHeaders(),
            body: JSON.stringify(requestBody),
          }
        );
        if (!resp.ok) {
          const errorData = await resp.json().catch(() => ({}));
          throw new Error(errorData.error || errorData.message || `Failed to update mapping: ${resp.status}`);
        }
        showSuccess('Mapping updated successfully');
      }
      setDialogOpen(false);
      setSelectionModel([]);
      await fetchMappings();
    } catch (err) {
      showError(err.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!canDelete || !rowPendingDelete?.id) return;
    setDeleteLoading(true);
    try {
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon-mapping-headers/${rowPendingDelete.id}`,
        { method: 'DELETE', headers: authHeaders() }
      );
      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `Failed to delete mapping: ${resp.status}`);
      }
      showSuccess('Record deleted successfully');
      setDeleteOpen(false);
      setRowPendingDelete(null);
      setSelectionModel([]);
      await fetchMappings();
    } catch (err) {
      showError(err.message || 'Delete failed');
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns = useMemo(() => [
    {
      field: 'hotel',
      headerName: 'Hotel',
      width: 240,
      resizable: true,
      valueGetter: (params) => {
        const property = propertyById[String(params.row.propertyId)];
        return propertyLabel(property) || params.row.propertyId || '';
      },
    },
    { field: 'bankAccountNo', headerName: 'Bank Account No', width: 180, resizable: true },
    {
      field: 'bankName',
      headerName: 'Bank',
      width: 180,
      resizable: true,
      valueGetter: (params) => bankNameById[String(params.row.bankId)] || '',
    },
    { field: 'monthYear', headerName: 'Month & Year', width: 140, resizable: true },
    {
      field: 'glAccount',
      headerName: 'GL Account',
      width: 260,
      resizable: true,
      valueGetter: (params) => {
        const code = params.row.glAccountNo || '';
        const name = params.row.glAccountName || '';
        return code && name ? `${code} - ${name}` : code || name || '';
      },
    },
  ], [bankNameById, propertyById]);

  const deleteLabel = rowPendingDelete
    ? `${propertyLabel(propertyById[String(rowPendingDelete.propertyId)]) || 'selected'}${rowPendingDelete.monthYear ? ` (${rowPendingDelete.monthYear})` : ''}`
    : 'selected';

  return (
    <Box sx={{ ...harmonatePageSx, height: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate(-1)} sx={harmonatePlainIconButtonSx} aria-label="Back">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={harmonatePageTitleSx}>Account Selection</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={canEdit ? 'Edit Mapping' : 'No edit permission'}>
            <span>
              <IconButton onClick={openEdit} disabled={!selectedRow || !canEdit} sx={harmonatePlainIconButtonSx}>
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={canAdd ? 'Account Selection' : 'No add permission'}>
            <span>
              <IconButton onClick={openAdd} disabled={!canAdd} sx={harmonatePlainIconButtonSx}>
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={canDelete ? 'Delete Mapping' : 'No delete permission'}>
            <span>
              <IconButton
                onClick={() => {
                  setRowPendingDelete(selectedRow);
                  setDeleteOpen(true);
                }}
                disabled={!selectedRow || !canDelete}
                sx={harmonatePlainIconButtonSx}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ ...harmonateCardSx, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <DataGridPro
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          rowHeight={40}
          pagination
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={setSelectionModel}
          sx={{
            ...harmonateDataGridSx,
            flex: 1,
            minHeight: 0,
            '& .MuiDataGrid-row:hover': { bgcolor: ht.secondaryTab, cursor: 'pointer' },
          }}
        />
      </Paper>

      <Dialog open={dialogOpen} onClose={() => !submitting && setDialogOpen(false)} maxWidth="md" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>{mode === 'add' ? 'Account Selection' : 'Edit Mapping'}</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Autocomplete
              fullWidth
              size="small"
              options={properties}
              getOptionLabel={propertyLabel}
              value={properties.find((p) => (p.PROPERTY_NAME || p.propertyName) === form.hotel) || null}
              onChange={(event, newValue) => {
                const hotelValue = newValue ? (newValue.PROPERTY_NAME || newValue.propertyName) : '';
                setForm({ ...form, hotel: hotelValue });
              }}
              disabled={propertiesLoading || mode === 'edit'}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Hotel"
                  size="small"
                  required
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {propertiesLoading ? <CircularProgress size={20} /> : params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) =>
                (option.PROPERTY_NAME || option.propertyName) === (value?.PROPERTY_NAME || value?.propertyName)
              }
            />
            <TextField
              fullWidth
              size="small"
              label="Month & Year"
              type="month"
              value={form.monthYear}
              onChange={(e) => setForm({ ...form, monthYear: e.target.value })}
              InputLabelProps={{ shrink: true }}
              required
              disabled={mode === 'edit'}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Bank Account No"
              placeholder={propertyAccountLoading ? 'Loading...' : 'Enter Bank Account No'}
              value={form.bankAccountNo || ''}
              onChange={(e) => setForm({ ...form, bankAccountNo: e.target.value })}
              required
              InputProps={{
                endAdornment: propertyAccountLoading ? <CircularProgress size={20} sx={{ ml: 1 }} /> : null,
              }}
            />
            <FormControl fullWidth size="small" required>
              <InputLabel required>Bank</InputLabel>
              <Select
                value={form.bank || ''}
                label="Bank"
                MenuProps={harmonateSelectMenuProps}
                onChange={(e) => setForm({ ...form, bank: e.target.value })}
                disabled={banksLoading || (mode === 'add' && propertyAccountLoading)}
                required
              >
                <MenuItem value="">Select Bank</MenuItem>
                {banks.map((bank) => (
                  <MenuItem key={bank.bankId} value={bank.bankId}>
                    {bank.bankName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Autocomplete
              fullWidth
              size="small"
              options={glAccounts}
              getOptionLabel={(option) => option.glAccount || `${option.glCode || ''} - ${option.glDescription || ''}`}
              value={glAccounts.find((a) => String(a.ledgerAccountId) === String(form.glAccountId)) || null}
              onChange={(event, newValue) => {
                setForm({ ...form, glAccountId: newValue ? newValue.ledgerAccountId : '' });
              }}
              disabled={glAccountsLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="GL Account"
                  size="small"
                  required
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {glAccountsLoading ? <CircularProgress size={20} /> : params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.ledgerAccountId === value?.ledgerAccountId}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={submitting} sx={harmonateProceedButtonSx}>{submitting ? 'Saving...' : mode === 'add' ? 'Save' : 'Update'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => !deleteLoading && setDeleteOpen(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Confirm Delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography sx={{ color: ht.text }}>Are you sure you want to delete &quot;{deleteLabel}&quot;?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleteLoading} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" disabled={deleteLoading} sx={harmonateAbortButtonSx}>{deleteLoading ? 'Deleting...' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>

      <Toast
        open={toastState.open}
        onClose={hideToast}
        message={toastState.message}
        severity={toastState.severity}
        title={toastState.title}
        autoHideDuration={toastState.autoHideDuration}
        position={toastState.position}
      />
    </Box>
  );
};

export default AccountMapping;
